import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { withRouter } from 'react-router-dom';
import { setPortal } from 'modules/portal';
import { dailyArticle, dailyArticleDetail } from 'lib/api/time';
import { makeRowColumn, getHeadObj, dayColor } from 'lib/commonLib';
import { paramConverter } from 'modules/common';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

import ArticleGraph from './ArticleGraph';
import { useDispatch, useSelector } from 'react-redux';

const initParams = {
  pltfomTpCode: 'all',
  itlTpCode: 'all',
  sort: 'desc',
  order: 'STATS_DTTM',
  sDate: moment().format('YYYYMM01'),
  eDate: moment().add(-1, 'day').format('YYYYMMDD'),
};
const Article = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));
  const [params, setParams] = useState(initParams);
  const [initialization, setInitialization] = useState(false);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    setInitialization(true);
    setParams(initParams);
  };

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  /**
   * 테이블 클릭 후 세부정보 받아올때 보낼 param setting 해주는 함수
   * @param {json} data 테이블 로우 데이터
   */
  const makeSubParam = (data) => {
    return {
      statsDttm: data.STATS_DTTM,
    };
  };

  // header 및 body 컬럼 커스텀
  const customized = {
    STATS_DTTM: {
      makeHead: (headsMap) => {
        return `${getHeadObj(headsMap, 'STATS_DTTM').label}`;
      },
      makeBody: (rowObj, headsMap) => {
        const statsDttm = getHeadObj(headsMap, 'STATS_DTTM');
        let totalCheck = rowObj.STATS_DTTM === 'total';
        const showWindowUrl =
          '/report/media/mediatarget?sDate=' +
          rowObj['STATS_DTTM'] +
          '&eDate=' +
          rowObj['STATS_DTTM'] +
          '&uri=/report/media/mediatarget&isPop=1';

        return (
          <IconAreaWrap
            text={dayColor(
              rowObj['STATS_DTTM'],
              statsDttm.type,
              statsDttm.format
            )}
            icon={!rowObj.isChild && !totalCheck && 'faExternalLink'}
            toolTipTitle={t('해당 날짜의 지면별 타게팅분포')}
            onClick={(e) => handlePortalUrl(showWindowUrl)}
          />
        );
      },
    },

    ARTICLE_PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        return {
          content: <>{getHeadObj(headsMap, 'ARTICLE_PAR_EPRS_CNT').label}</>,
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const firstAdvrtsAmt = getHeadObj(headsMap, 'ARTICLE_PAR_EPRS_CNT');
        const secondAdvrtsAmt = getHeadObj(headsMap, 'AVG_FRM_ARTICLE_CNT');
        return (
          <>
            <p>
              {makeRowColumn(
                rowObj['ARTICLE_PAR_EPRS_CNT'],
                firstAdvrtsAmt.type,
                firstAdvrtsAmt.format
              )}
            </p>
            <p title="프레임 별 평균 기사 개수">
              (
              {makeRowColumn(
                rowObj['AVG_FRM_ARTICLE_CNT'],
                secondAdvrtsAmt.type,
                secondAdvrtsAmt.format
              )}
              )
            </p>
          </>
        );
      },
    },
    AVG_FRM_ARTICLE_CNT: {
      merged: true,
    },
    RE_ARTICLE_OCC_RATE: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={t('3개월 추천연관 기사')}
                tooltipTitle={
                  '유저가 클릭한 기사의 핵심 키워드를 추출하여 최근 3개월 동안 수집된 기사 중 핵심 키워드가 동일한 기사 매칭'
                }
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const firstAdvrtsAmt = getHeadObj(headsMap, 'RE_ARTICLE_OCC_RATE');
        const secondAdvrtsAmt = getHeadObj(headsMap, 'RE_ARTICLE_CLICK_CNT');
        const thirdAdvrtsAmt = getHeadObj(headsMap, 'RE_ARTICLE_CLICK_RATE');
        const fourthAdvrtsAmt = getHeadObj(headsMap, 'RE_ARTICLE_PAR_EPRS_CNT');
        return (
          <>
            <p
              title={
                getHeadObj(headsMap, 'AM_ARTICLE_PAR_EPRS_CNT').label +
                '(' +
                getHeadObj(headsMap, 'RE_ARTICLE_OCC_RATE').label +
                ')'
              }
            >
              {makeRowColumn(
                rowObj['RE_ARTICLE_PAR_EPRS_CNT'],
                fourthAdvrtsAmt.type,
                fourthAdvrtsAmt.format
              )}
              (
              {makeRowColumn(
                rowObj['RE_ARTICLE_OCC_RATE'],
                firstAdvrtsAmt.type,
                firstAdvrtsAmt.format
              )}
              )
            </p>
            <p title={getHeadObj(headsMap, 'RE_ARTICLE_CLICK_CNT').label}>
              {makeRowColumn(
                rowObj['RE_ARTICLE_CLICK_CNT'],
                secondAdvrtsAmt.type,
                secondAdvrtsAmt.format
              )}
            </p>
            <p
              className={'blue'}
              title={getHeadObj(headsMap, 'RE_ARTICLE_CLICK_RATE').label}
            >
              {makeRowColumn(
                rowObj['RE_ARTICLE_CLICK_RATE'],
                thirdAdvrtsAmt.type,
                thirdAdvrtsAmt.format
              )}
            </p>
          </>
        );
      },
    },
    RE_ARTICLE_PAR_EPRS_CNT: {
      merged: true,
    },
    RE_ARTICLE_CLICK_CNT: {
      merged: true,
    },
    RE_ARTICLE_CLICK_RATE: {
      merged: true,
    },

    RH_ARTICLE_OCC_RATE: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={t('월간 추천연관 기사')}
                tooltipTitle={t(
                  '유저가 클릭한 기사의 핵심 키워드를 추출하여 최근 1개월 동안 수집된 기사 중 핵심 키워드가 동일한 기사 매칭'
                )}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const firstAdvrtsAmt = getHeadObj(headsMap, 'RH_ARTICLE_OCC_RATE');
        const secondAdvrtsAmt = getHeadObj(headsMap, 'RH_ARTICLE_CLICK_CNT');
        const thirdAdvrtsAmt = getHeadObj(headsMap, 'RH_ARTICLE_CLICK_RATE');
        const fourthAdvrtsAmt = getHeadObj(headsMap, 'RH_ARTICLE_PAR_EPRS_CNT');
        return (
          <>
            <p
              title={
                getHeadObj(headsMap, 'RH_ARTICLE_PAR_EPRS_CNT').label +
                '(' +
                getHeadObj(headsMap, 'RH_ARTICLE_OCC_RATE').label +
                ')'
              }
            >
              {makeRowColumn(
                rowObj['RH_ARTICLE_PAR_EPRS_CNT'],
                fourthAdvrtsAmt.type,
                fourthAdvrtsAmt.format
              )}
              (
              {makeRowColumn(
                rowObj['RH_ARTICLE_OCC_RATE'],
                firstAdvrtsAmt.type,
                firstAdvrtsAmt.format
              )}
              )
            </p>
            <p title={getHeadObj(headsMap, 'RH_ARTICLE_CLICK_CNT').label}>
              {makeRowColumn(
                rowObj['RH_ARTICLE_CLICK_CNT'],
                secondAdvrtsAmt.type,
                secondAdvrtsAmt.format
              )}
            </p>
            <p
              className={'blue'}
              title={getHeadObj(headsMap, 'RH_ARTICLE_CLICK_RATE').label}
            >
              {makeRowColumn(
                rowObj['RH_ARTICLE_CLICK_RATE'],
                thirdAdvrtsAmt.type,
                thirdAdvrtsAmt.format
              )}
            </p>
          </>
        );
      },
    },
    RH_ARTICLE_PAR_EPRS_CNT: {
      merged: true,
    },
    RH_ARTICLE_CLICK_CNT: {
      merged: true,
    },
    RH_ARTICLE_CLICK_RATE: {
      merged: true,
    },

    RW_ARTICLE_OCC_RATE: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={t('주간 추천연관 기사')}
                tooltipTitle={t(
                  '유저가 클릭한 기사의 핵심 키워드를 추출하여 최근 1주일 동안 수집된 기사 중 핵심 키워드가 동일한 기사 매칭'
                )}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const firstAdvrtsAmt = getHeadObj(headsMap, 'RW_ARTICLE_OCC_RATE');
        const secondAdvrtsAmt = getHeadObj(headsMap, 'RW_ARTICLE_CLICK_CNT');
        const thirdAdvrtsAmt = getHeadObj(headsMap, 'RW_ARTICLE_CLICK_RATE');
        const fourthAdvrtsAmt = getHeadObj(headsMap, 'RW_ARTICLE_PAR_EPRS_CNT');
        return (
          <>
            <p
              title={
                getHeadObj(headsMap, 'RW_ARTICLE_PAR_EPRS_CNT').label +
                '(' +
                getHeadObj(headsMap, 'RW_ARTICLE_OCC_RATE').label +
                ')'
              }
            >
              {makeRowColumn(
                rowObj['RW_ARTICLE_PAR_EPRS_CNT'],
                fourthAdvrtsAmt.type,
                fourthAdvrtsAmt.format
              )}
              (
              {makeRowColumn(
                rowObj['RW_ARTICLE_OCC_RATE'],
                firstAdvrtsAmt.type,
                firstAdvrtsAmt.format
              )}
              )
            </p>
            <p title={getHeadObj(headsMap, 'RW_ARTICLE_CLICK_CNT').label}>
              {makeRowColumn(
                rowObj['RW_ARTICLE_CLICK_CNT'],
                secondAdvrtsAmt.type,
                secondAdvrtsAmt.format
              )}
            </p>
            <p
              className={'blue'}
              title={getHeadObj(headsMap, 'RW_ARTICLE_CLICK_RATE').label}
            >
              {makeRowColumn(
                rowObj['RW_ARTICLE_CLICK_RATE'],
                thirdAdvrtsAmt.type,
                thirdAdvrtsAmt.format
              )}
            </p>
          </>
        );
      },
    },
    RW_ARTICLE_PAR_EPRS_CNT: {
      merged: true,
    },
    RW_ARTICLE_CLICK_CNT: {
      merged: true,
    },
    RW_ARTICLE_CLICK_RATE: {
      merged: true,
    },

    RM_ARTICLE_OCC_RATE: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={t('리턴기사')}
                tooltipTitle={t(
                  '최근 한 달 동안 유저가 마지막으로 본 기사와 동일한 핵심 키워드를 가지는 기사 중 최신 기사 매칭'
                )}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const firstAdvrtsAmt = getHeadObj(headsMap, 'RM_ARTICLE_OCC_RATE');
        const secondAdvrtsAmt = getHeadObj(headsMap, 'RM_ARTICLE_CLICK_CNT');
        const thirdAdvrtsAmt = getHeadObj(headsMap, 'RM_ARTICLE_CLICK_RATE');
        const fourthAdvrtsAmt = getHeadObj(headsMap, 'RM_ARTICLE_PAR_EPRS_CNT');
        return (
          <>
            <p
              title={
                getHeadObj(headsMap, 'RM_ARTICLE_PAR_EPRS_CNT').label +
                '(' +
                getHeadObj(headsMap, 'RM_ARTICLE_OCC_RATE').label +
                ')'
              }
            >
              {makeRowColumn(
                rowObj['RM_ARTICLE_PAR_EPRS_CNT'],
                fourthAdvrtsAmt.type,
                fourthAdvrtsAmt.format
              )}
              (
              {makeRowColumn(
                rowObj['RM_ARTICLE_OCC_RATE'],
                firstAdvrtsAmt.type,
                firstAdvrtsAmt.format
              )}
              )
            </p>
            <p title={getHeadObj(headsMap, 'RM_ARTICLE_CLICK_CNT').label}>
              {makeRowColumn(
                rowObj['RM_ARTICLE_CLICK_CNT'],
                secondAdvrtsAmt.type,
                secondAdvrtsAmt.format
              )}
            </p>
            <p
              className={'blue'}
              title={getHeadObj(headsMap, 'RM_ARTICLE_CLICK_RATE').label}
            >
              {makeRowColumn(
                rowObj['RM_ARTICLE_CLICK_RATE'],
                thirdAdvrtsAmt.type,
                thirdAdvrtsAmt.format
              )}
            </p>
          </>
        );
      },
    },
    RM_ARTICLE_PAR_EPRS_CNT: {
      merged: true,
    },
    RM_ARTICLE_CLICK_CNT: {
      merged: true,
    },
    RM_ARTICLE_CLICK_RATE: {
      merged: true,
    },

    AU_ARTICLE_OCC_RATE: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={t('3개월 오디언스 기사')}
                tooltipTitle={t(
                  '최근 3개월 동안 수집된 기사 중 유저의 오디언스매칭와 동일한 카테고리의 최신 기사 매칭'
                )}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const firstAdvrtsAmt = getHeadObj(headsMap, 'AU_ARTICLE_OCC_RATE');
        const secondAdvrtsAmt = getHeadObj(headsMap, 'AU_ARTICLE_CLICK_CNT');
        const thirdAdvrtsAmt = getHeadObj(headsMap, 'AU_ARTICLE_CLICK_RATE');
        const fourthAdvrtsAmt = getHeadObj(headsMap, 'AU_ARTICLE_PAR_EPRS_CNT');
        return (
          <>
            <p
              title={
                getHeadObj(headsMap, 'AU_ARTICLE_PAR_EPRS_CNT').label +
                '(' +
                getHeadObj(headsMap, 'AU_ARTICLE_OCC_RATE').label +
                ')'
              }
            >
              {makeRowColumn(
                rowObj['AU_ARTICLE_PAR_EPRS_CNT'],
                fourthAdvrtsAmt.type,
                fourthAdvrtsAmt.format
              )}
              (
              {makeRowColumn(
                rowObj['AU_ARTICLE_OCC_RATE'],
                firstAdvrtsAmt.type,
                firstAdvrtsAmt.format
              )}
              )
            </p>
            <p title={getHeadObj(headsMap, 'AU_ARTICLE_CLICK_CNT').label}>
              {makeRowColumn(
                rowObj['AU_ARTICLE_CLICK_CNT'],
                secondAdvrtsAmt.type,
                secondAdvrtsAmt.format
              )}
            </p>
            <p
              className={'blue'}
              title={getHeadObj(headsMap, 'AU_ARTICLE_CLICK_RATE').label}
            >
              {makeRowColumn(
                rowObj['AU_ARTICLE_CLICK_RATE'],
                thirdAdvrtsAmt.type,
                thirdAdvrtsAmt.format
              )}
            </p>
          </>
        );
      },
    },
    AU_ARTICLE_PAR_EPRS_CNT: {
      merged: true,
    },
    AU_ARTICLE_CLICK_CNT: {
      merged: true,
    },
    AU_ARTICLE_CLICK_RATE: {
      merged: true,
    },

    AM_ARTICLE_OCC_RATE: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={t('월간 오디언스 기사')}
                tooltipTitle={t(
                  '최근 1개월 동안 수집된 기사 중 유저의 오디언스매칭와 동일한 카테고리의 최신 기사 매칭'
                )}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const firstAdvrtsAmt = getHeadObj(headsMap, 'AM_ARTICLE_OCC_RATE');
        const secondAdvrtsAmt = getHeadObj(headsMap, 'AM_ARTICLE_CLICK_CNT');
        const thirdAdvrtsAmt = getHeadObj(headsMap, 'AM_ARTICLE_CLICK_RATE');
        const fourthAdvrtsAmt = getHeadObj(headsMap, 'AM_ARTICLE_PAR_EPRS_CNT');
        return (
          <>
            <p
              title={
                getHeadObj(headsMap, 'AM_ARTICLE_PAR_EPRS_CNT').label +
                '(' +
                getHeadObj(headsMap, 'AM_ARTICLE_OCC_RATE').label +
                ')'
              }
            >
              {makeRowColumn(
                rowObj['AM_ARTICLE_PAR_EPRS_CNT'],
                fourthAdvrtsAmt.type,
                fourthAdvrtsAmt.format
              )}
              (
              {makeRowColumn(
                rowObj['AM_ARTICLE_OCC_RATE'],
                firstAdvrtsAmt.type,
                firstAdvrtsAmt.format
              )}
              )
            </p>
            <p title={getHeadObj(headsMap, 'AM_ARTICLE_CLICK_CNT').label}>
              {makeRowColumn(
                rowObj['AM_ARTICLE_CLICK_CNT'],
                secondAdvrtsAmt.type,
                secondAdvrtsAmt.format
              )}
            </p>
            <p
              className={'blue'}
              title={getHeadObj(headsMap, 'AM_ARTICLE_CLICK_RATE').label}
            >
              {makeRowColumn(
                rowObj['AM_ARTICLE_CLICK_RATE'],
                thirdAdvrtsAmt.type,
                thirdAdvrtsAmt.format
              )}
            </p>
          </>
        );
      },
    },
    AM_ARTICLE_PAR_EPRS_CNT: {
      merged: true,
    },
    AM_ARTICLE_CLICK_CNT: {
      merged: true,
    },
    AM_ARTICLE_CLICK_RATE: {
      merged: true,
    },

    CM_ARTICLE_OCC_RATE: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={t('카테고리 기사')}
                tooltipTitle={t(
                  '유저가 클릭한 기사의 카테고리와 동일한 카테고리를 가지는 최신 기사 매칭'
                )}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const firstAdvrtsAmt = getHeadObj(headsMap, 'CM_ARTICLE_OCC_RATE');
        const secondAdvrtsAmt = getHeadObj(headsMap, 'CM_ARTICLE_CLICK_CNT');
        const thirdAdvrtsAmt = getHeadObj(headsMap, 'CM_ARTICLE_CLICK_RATE');
        const fourthAdvrtsAmt = getHeadObj(headsMap, 'CM_ARTICLE_PAR_EPRS_CNT');
        return (
          <>
            <p
              title={
                getHeadObj(headsMap, 'CM_ARTICLE_PAR_EPRS_CNT').label +
                '(' +
                getHeadObj(headsMap, 'CM_ARTICLE_OCC_RATE').label +
                ')'
              }
            >
              {makeRowColumn(
                rowObj['CM_ARTICLE_PAR_EPRS_CNT'],
                fourthAdvrtsAmt.type,
                fourthAdvrtsAmt.format
              )}
              (
              {makeRowColumn(
                rowObj['CM_ARTICLE_OCC_RATE'],
                firstAdvrtsAmt.type,
                firstAdvrtsAmt.format
              )}
              )
            </p>
            <p title={getHeadObj(headsMap, 'CM_ARTICLE_CLICK_CNT').label}>
              {makeRowColumn(
                rowObj['CM_ARTICLE_CLICK_CNT'],
                secondAdvrtsAmt.type,
                secondAdvrtsAmt.format
              )}
            </p>
            <p
              className={'blue'}
              title={getHeadObj(headsMap, 'CM_ARTICLE_CLICK_RATE').label}
            >
              {makeRowColumn(
                rowObj['CM_ARTICLE_CLICK_RATE'],
                thirdAdvrtsAmt.type,
                thirdAdvrtsAmt.format
              )}
            </p>
          </>
        );
      },
    },
    CM_ARTICLE_PAR_EPRS_CNT: {
      merged: true,
    },
    CM_ARTICLE_CLICK_CNT: {
      merged: true,
    },
    CM_ARTICLE_CLICK_RATE: {
      merged: true,
    },

    PO_ARTICLE_OCC_RATE: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={t('주간 인기기사')}
                tooltipTitle={t(
                  '해당 매체의 최근 7일 기준 기사별 total view count 가 가장 높은 기사 매칭'
                )}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const firstAdvrtsAmt = getHeadObj(headsMap, 'PO_ARTICLE_OCC_RATE');
        const secondAdvrtsAmt = getHeadObj(headsMap, 'PO_ARTICLE_CLICK_CNT');
        const thirdAdvrtsAmt = getHeadObj(headsMap, 'PO_ARTICLE_CLICK_RATE');
        const fourthAdvrtsAmt = getHeadObj(headsMap, 'PO_ARTICLE_PAR_EPRS_CNT');
        return (
          <>
            <p
              title={
                getHeadObj(headsMap, 'PO_ARTICLE_PAR_EPRS_CNT').label +
                '(' +
                getHeadObj(headsMap, 'PO_ARTICLE_OCC_RATE').label +
                ')'
              }
            >
              {makeRowColumn(
                rowObj['PO_ARTICLE_PAR_EPRS_CNT'],
                fourthAdvrtsAmt.type,
                fourthAdvrtsAmt.format
              )}
              {makeRowColumn(
                rowObj['PO_ARTICLE_OCC_RATE'],
                firstAdvrtsAmt.type,
                firstAdvrtsAmt.format
              )}
            </p>
            <p title={getHeadObj(headsMap, 'PO_ARTICLE_CLICK_CNT').label}>
              {makeRowColumn(
                rowObj['PO_ARTICLE_CLICK_CNT'],
                secondAdvrtsAmt.type,
                secondAdvrtsAmt.format
              )}
            </p>
            <p
              className={'blue'}
              title={getHeadObj(headsMap, 'PO_ARTICLE_CLICK_RATE').label}
            >
              {makeRowColumn(
                rowObj['PO_ARTICLE_CLICK_RATE'],
                thirdAdvrtsAmt.type,
                thirdAdvrtsAmt.format
              )}
            </p>
          </>
        );
      },
    },
    PO_ARTICLE_PAR_EPRS_CNT: {
      merged: true,
    },
    PO_ARTICLE_CLICK_CNT: {
      merged: true,
    },
    PO_ARTICLE_CLICK_RATE: {
      merged: true,
    },

    LA_ARTICLE_OCC_RATE: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              <InfoTitleArea
                title={t('최신 기사')}
                tooltipTitle={t(
                  '해당 매체에서 최근 3일 동안 수집된 기사 중 가장 최신 기사 매칭'
                )}
                iconType="fainfoCircle"
              />
            </>
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const firstAdvrtsAmt = getHeadObj(headsMap, 'LA_ARTICLE_OCC_RATE');
        const secondAdvrtsAmt = getHeadObj(headsMap, 'LA_ARTICLE_CLICK_CNT');
        const thirdAdvrtsAmt = getHeadObj(headsMap, 'LA_ARTICLE_CLICK_RATE');
        const fourthAdvrtsAmt = getHeadObj(headsMap, 'LA_ARTICLE_PAR_EPRS_CNT');
        return (
          <>
            <p
              title={
                getHeadObj(headsMap, 'LA_ARTICLE_PAR_EPRS_CNT').label +
                '(' +
                getHeadObj(headsMap, 'LA_ARTICLE_OCC_RATE').label +
                ')'
              }
            >
              {makeRowColumn(
                rowObj['LA_ARTICLE_PAR_EPRS_CNT'],
                fourthAdvrtsAmt.type,
                fourthAdvrtsAmt.format
              )}
              (
              {makeRowColumn(
                rowObj['LA_ARTICLE_OCC_RATE'],
                firstAdvrtsAmt.type,
                firstAdvrtsAmt.format
              )}
              )
            </p>
            <p title={getHeadObj(headsMap, 'LA_ARTICLE_CLICK_CNT').label}>
              {makeRowColumn(
                rowObj['LA_ARTICLE_CLICK_CNT'],
                secondAdvrtsAmt.type,
                secondAdvrtsAmt.format
              )}
            </p>
            <p
              className={'blue'}
              title={getHeadObj(headsMap, 'LA_ARTICLE_CLICK_RATE').label}
            >
              {makeRowColumn(
                rowObj['LA_ARTICLE_CLICK_RATE'],
                thirdAdvrtsAmt.type,
                thirdAdvrtsAmt.format
              )}
            </p>
          </>
        );
      },
    },
    LA_ARTICLE_PAR_EPRS_CNT: {
      merged: true,
    },
    LA_ARTICLE_CLICK_CNT: {
      merged: true,
    },
    LA_ARTICLE_CLICK_RATE: {
      merged: true,
    },

    ARTICLE_OCC_RATE: {
      merged: true,
    },
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          useDisplayFilter={false}
        />
      </NewFilterWrap>
      <ArticleGraph params={{ ...paramConverter(params) }} />
      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={dailyArticle}
        dataDetailReqPromise={dailyArticleDetail}
        makeSubParam={makeSubParam}
        customized={customized}
        expandTable
        useSearch={false}
      />
    </>
  );
};

export default withRouter(Article);
